import { useEffect, useState } from "react";
import { Check, Loader2, Pause, XCircle } from "lucide-react";
import { Field, FieldGrid } from "@/components/shared/form";
import { StatusDot } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import { QUALITY_STATUSES, labelFor, qualityTone } from "@/features/inventory/constants";
import type { QualityStatus } from "@/features/inventory/types";
import { useVerifyBatchQuality } from "@/features/quality/api";
import type { QcQueueRow } from "@/features/quality/types";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  row: QcQueueRow | null;
};

export function VerifyQualitySheet({ open, onOpenChange, row }: Props) {
  const verify = useVerifyBatchQuality();
  const [notes, setNotes] = useState("");
  const [status, setStatus] = useState<QualityStatus>("passed");

  useEffect(() => {
    if (!open || !row) return;
    setNotes(row.qc_notes ?? "");
    setStatus(row.quality_status === "hold" ? "hold" : "passed");
  }, [open, row]);

  async function submit(next: QualityStatus) {
    if (!row) return;
    setStatus(next);
    await verify.mutateAsync({
      batchId: row.id,
      quality_status: next,
      qc_notes: notes,
    });
    onOpenChange(false);
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-md">
        <SheetHeader>
          <SheetTitle>Verify {row?.batch_no}</SheetTitle>
          <SheetDescription>
            {[row?.material_name, row?.grade_name, row?.shape_label].filter(Boolean).join(" · ") ||
              "Batch quality check"}
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 space-y-3 overflow-y-auto p-4">
          {row ? (
            <div className="space-y-1 text-xs">
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground">Current</span>
                <StatusDot tone={qualityTone(row.quality_status)}>
                  {labelFor(QUALITY_STATUSES, row.quality_status)}
                </StatusDot>
              </div>
              {row.plan_no ? (
                <div className="text-muted-foreground">
                  Plan {row.plan_no}
                  {row.process_name ? ` · ${row.process_name}` : ""}
                </div>
              ) : null}
              <div className="text-muted-foreground">
                {row.location_code ?? "—"} · wt {row.weight} · qty {row.quantity}
              </div>
            </div>
          ) : null}

          <FieldGrid>
            <Field label="QC notes" span={12}>
              <Textarea
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Remarks, defects, rework hints…"
              />
            </Field>
          </FieldGrid>
        </div>

        <SheetFooter className="flex-wrap gap-1.5">
          <Button type="button" variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={verify.isPending || !row}
            onClick={() => void submit("hold")}
          >
            {verify.isPending && status === "hold" ? <Loader2 className="animate-spin" /> : <Pause />}
            Hold
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="text-destructive"
            disabled={verify.isPending || !row}
            onClick={() => void submit("rejected")}
          >
            {verify.isPending && status === "rejected" ? (
              <Loader2 className="animate-spin" />
            ) : (
              <XCircle />
            )}
            Reject
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={verify.isPending || !row}
            onClick={() => void submit("passed")}
          >
            {verify.isPending && status === "passed" ? <Loader2 className="animate-spin" /> : <Check />}
            Pass
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
